import type { EChartsOption } from 'echarts';
import { useMemo } from 'react';

import { seriesColor, useTheme } from '../../theme/readTheme';
import type { ChartData, ChartFields } from '../../types';
import { EChart } from '../EChart';
import { Plot } from '../Plot';
import { Empty } from '../states';
import { buildFrame, frameNote, isEmpty } from '../frame';
import { compact } from '../format';
import { animation, itemTooltip, legendFor, SURFACE_GAP } from '../options';

/**
 * A funnel — stages of a process narrowing to an outcome, each band's width its
 * share of the top. It reads as a sequence with drop-off, which a bar chart of
 * the same numbers does not: the taper is the point.
 *
 * Categories become the stages in query order (not re-sorted — a funnel's order
 * is the process, not the magnitude), each with its value. Capped at eight
 * bands so the labels stay legible.
 */

interface Props {
  data: ChartData;
  /** Author's column choices; absent means infer. */
  fields?: ChartFields;
}

const MAX_STAGES = 8;

const funnelView = (data: ChartData, theme: ReturnType<typeof useTheme>, fields?: ChartFields) => {
  const frame = buildFrame(data, { limit: MAX_STAGES, ...fields });
  if (isEmpty(frame)) return null;

  const values = frame.series[0].values;
  const stages = frame.categories
    .map((name, i) => ({ name, value: values[i] ?? 0 }))
    .filter((s) => s.value > 0);
  if (!stages.length) return null;

  const option: EChartsOption = {
    ...animation,
    legend: legendFor(theme, stages.length),
    tooltip: itemTooltip(theme, { showPercent: true }),
    series: [
      {
        type: 'funnel' as const,
        // Ordering follows the query — the process order — not the magnitude.
        sort: 'none' as const,
        top: 8,
        bottom: 8,
        left: '8%',
        right: '8%',
        gap: SURFACE_GAP,
        minSize: '18%',
        label: {
          show: true,
          position: 'inside' as const,
          color: '#fff',
          fontSize: 11,
          fontFamily: theme.font,
          formatter: (p: { name?: string; value?: unknown }) =>
            `${p.name ?? ''}  ${compact(Number(p.value) || 0)}`,
        },
        labelLine: { show: false },
        data: stages.map((s, i) => ({
          ...s,
          itemStyle: { color: seriesColor(theme, i), borderColor: theme.surface, borderWidth: SURFACE_GAP },
        })),
        emphasis: { label: { fontWeight: 'bold' as const } },
      },
    ],
  };

  return { option, note: frameNote(frame) };
};

export const FunnelChart = ({ data, fields }: Props) => {
  const theme = useTheme();
  const view = useMemo(() => funnelView(data, theme, fields), [data, theme, fields]);
  if (!view) return <Empty message="No positive values to chart" />;
  return (
    <Plot note={view.note}>
      <EChart option={view.option} ariaLabel="Funnel chart" />
    </Plot>
  );
};
